import { logStartGreyError } from "owa-analytics-start";
import type { FirstPartyMailboxProvider, MailboxInfo } from "owa-client-types";

/**
 * Attempts to get the tenant based routing hint for the AAD or MSA account
 * @param mailboxInfo Specifies the MailboxInfo for which the routing hint is to be determined
 * @returns The routing hint for the tenant or undefined if the routing hint cannot be determined
 */
function getRoutingHintForFirstPartyAccount(mailboxInfo: MailboxInfo, provider: FirstPartyMailboxProvider): string | undefined {
    const canUsePuid = mailboxInfo.type == "UserMailbox" && !mailboxInfo.isExplicitLogon;
    if (!!mailboxInfo.tenantId) {
        if (!!mailboxInfo.PUID && canUsePuid) {
            return `PUID:${mailboxInfo.PUID}@${mailboxInfo.tenantId}`;
        }
        else if (!!mailboxInfo.externalDirectoryObjectId) {
            return `OID:${mailboxInfo.externalDirectoryObjectId}@${mailboxInfo.tenantId}`;
        }
        else if (!!mailboxInfo.auxiliaryMailboxGuid) {
            return `MBX:${mailboxInfo.auxiliaryMailboxGuid}@${mailboxInfo.tenantId}`;
        }
    }
    
    // could not determine the routing hint for the tenant, fallback to using the SMTP address
    const error = new Error("FailedToGetRoutingHintForFirstPartyAccount");
    
    logStartGreyError("FailedToGetRoutingHintForFirstPartyAccount", mailboxInfo, error, {
        provider,
    });
    
    switch (provider) {
        case "Office365":
            if (mailboxInfo.type === "UserPrincipalName") {
                return `AAD-UPN:${mailboxInfo.userIdentity}`;
            }
            
            return `AAD-SMTP:${mailboxInfo.mailboxSmtpAddress}`;
        
        case "Outlook":
            return `MSA:${mailboxInfo.mailboxSmtpAddress}`;
        
        default:
            return undefined;
    }
}

/**
 * Attempts to get an unambiguous anchor mailbox from the properties of a mailbox info object
 * @param mailboxInfo Specifies the mailbox for which the anchor mailbox is to be determined
 * @returns string representing the anchor mailbox or undefined if the anchor mailbox cannot be determined
 */
export function getAnchorMailboxFromMailboxInfo(mailboxInfo?: MailboxInfo): string | undefined {
    if (!mailboxInfo) {
        // We cannot build the anchor mailbox when there is no mailbox info
        return undefined;
    }
    
    switch (mailboxInfo.mailboxProvider) {
        case "Google":
        case "ICloud":
        case "Yahoo":
        case "IMAP":
        case "POP3":
            // The user identity of the cloud cache account will inlucde the CID and is an unabiguous anchor mailbox
            return mailboxInfo.userIdentity;
        
        case "Office365":
        case "Outlook":
            return getRoutingHintForFirstPartyAccount(mailboxInfo, mailboxInfo.mailboxProvider);
        
        case "PstFile":
        case "Unknown":
        default:
            return undefined;
    }
}
